import React, { useState, useEffect } from 'react';
import { 
  FileText, Plus, Pencil, Trash2, Check, X, Shield, 
  AlertTriangle, AlertOctagon, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { getPolicies, createPolicy, updatePolicy, deletePolicy } from '../services/api';
import { toast } from 'sonner';

const PoliciesPage = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    rule_type: 'action_restriction',
    conditions: {},
    action: 'block_and_alert',
    severity: 'high',
    enabled: true
  });

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const response = await getPolicies();
      setPolicies(response.data);
    } catch (error) {
      console.error('Failed to fetch policies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPolicy) {
        await updatePolicy(editingPolicy.id, formData);
        toast.success('Policy updated successfully');
      } else {
        await createPolicy(formData);
        toast.success('Policy created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchPolicies();
    } catch (error) {
      toast.error('Failed to save policy');
    }
  };

  const handleDelete = async (policyId) => {
    try {
      await deletePolicy(policyId);
      toast.success('Policy deleted');
      fetchPolicies();
    } catch (error) {
      toast.error('Failed to delete policy');
    }
  };

  const handleEdit = (policy) => {
    setEditingPolicy(policy);
    setFormData({
      name: policy.name,
      description: policy.description,
      rule_type: policy.rule_type,
      conditions: policy.conditions,
      action: policy.action,
      severity: policy.severity,
      enabled: policy.enabled
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPolicy(null);
    setFormData({
      name: '',
      description: '',
      rule_type: 'action_restriction',
      conditions: {},
      action: 'block_and_alert',
      severity: 'high',
      enabled: true
    });
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <AlertOctagon className="w-4 h-4 text-[#FF3B30]" />;
      case 'high': return <AlertTriangle className="w-4 h-4 text-[#FF6961]" />;
      case 'medium': return <AlertCircle className="w-4 h-4 text-[#FFCC00]" />;
      default: return <Shield className="w-4 h-4 text-[#0066FF]" />;
    }
  };

  const getSeverityClass = (severity) => {
    switch (severity) {
      case 'critical': return 'risk-critical';
      case 'high': return 'risk-high';
      case 'medium': return 'risk-medium';
      default: return 'risk-low';
    }
  };

  const getRuleTypeLabel = (type) => {
    const labels = {
      'action_restriction': 'Action Restriction',
      'environment_restriction': 'Environment Restriction',
      'governance': 'Governance',
      'data_restriction': 'Data Restriction',
      'tool_restriction': 'Tool Restriction',
      'credential_hygiene': 'Credential Hygiene',
    };
    return labels[type] || type;
  };

  return (
    <div data-testid="policies-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#F9FAFB] mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Policy Engine
          </h1>
          <p className="text-[#9CA3AF]">
            Define and manage security policies for your AI agents.
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="primary-btn" data-testid="create-policy-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Policy
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-[#27272A] max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-[#F9FAFB]">
                {editingPolicy ? 'Edit Policy' : 'Create New Policy'}
              </DialogTitle>
              <DialogDescription className="text-[#9CA3AF]">
                Define policy rules to govern agent behavior.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#9CA3AF]">Policy Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none"
                  placeholder="e.g., No Production Writes"
                  required
                  data-testid="policy-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#9CA3AF]">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none"
                  placeholder="Describe what this policy enforces..."
                  required
                  data-testid="policy-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#9CA3AF]">Rule Type</Label>
                  <Select 
                    value={formData.rule_type} 
                    onValueChange={(v) => setFormData({ ...formData, rule_type: v })}
                  >
                    <SelectTrigger className="bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                      <SelectItem value="action_restriction">Action Restriction</SelectItem>
                      <SelectItem value="environment_restriction">Environment Restriction</SelectItem>
                      <SelectItem value="governance">Governance</SelectItem>
                      <SelectItem value="data_restriction">Data Restriction</SelectItem>
                      <SelectItem value="tool_restriction">Tool Restriction</SelectItem>
                      <SelectItem value="credential_hygiene">Credential Hygiene</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#9CA3AF]">Severity</Label>
                  <Select 
                    value={formData.severity} 
                    onValueChange={(v) => setFormData({ ...formData, severity: v })}
                  >
                    <SelectTrigger className="bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#9CA3AF]">Action</Label>
                <Select 
                  value={formData.action} 
                  onValueChange={(v) => setFormData({ ...formData, action: v })}
                >
                  <SelectTrigger className="bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                    <SelectItem value="block_and_alert">Block & Alert</SelectItem>
                    <SelectItem value="block">Block Only</SelectItem>
                    <SelectItem value="alert">Alert Only</SelectItem>
                    <SelectItem value="require_approval">Require Approval</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-[#9CA3AF]">Enabled</Label>
                <Switch
                  checked={formData.enabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
                  data-testid="policy-enabled-switch"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="secondary-btn">
                  Cancel
                </Button>
                <Button type="submit" className="primary-btn" data-testid="save-policy-btn">
                  {editingPolicy ? 'Update Policy' : 'Create Policy'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Policies Grid */}
      {loading ? (
        <div className="enterprise-card p-8 text-center text-[#9CA3AF]">Loading policies...</div>
      ) : policies.length === 0 ? (
        <div className="enterprise-card p-12 text-center">
          <FileText className="w-12 h-12 text-[#6B7280] mx-auto mb-4" />
          <p className="text-[#9CA3AF] mb-4">No policies configured yet.</p>
          <Button onClick={() => setIsDialogOpen(true)} className="primary-btn">
            Create Your First Policy
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {policies.map((policy) => (
            <div 
              key={policy.id} 
              className={`enterprise-card p-6 ${!policy.enabled ? 'opacity-60' : ''}`}
              data-testid={`policy-${policy.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {getSeverityIcon(policy.severity)}
                  <div>
                    <h3 className="text-lg font-bold text-[#F9FAFB]">{policy.name}</h3>
                    <span className="text-xs text-[#6B7280]">{getRuleTypeLabel(policy.rule_type)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={getSeverityClass(policy.severity)}>{policy.severity}</span>
                  <span className={`px-2 py-0.5 text-xs font-mono ${
                    policy.enabled 
                      ? 'text-[#34C759] bg-[#34C759]/10' 
                      : 'text-[#6B7280] bg-[#6B7280]/10'
                  }`}>
                    {policy.enabled ? 'ACTIVE' : 'DISABLED'}
                  </span>
                </div>
              </div>
              <p className="text-[#9CA3AF] text-sm mb-4">{policy.description}</p>
              <div className="flex items-center justify-between">
                <div className="text-xs text-[#6B7280]">
                  <span className="font-mono">{policy.violations_count}</span> violations detected
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(policy)}
                    className="text-[#9CA3AF] hover:text-[#F9FAFB]"
                    data-testid={`edit-policy-${policy.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#9CA3AF] hover:text-[#FF3B30]"
                        data-testid={`delete-policy-${policy.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-[#121212] border-[#27272A]">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-[#F9FAFB]">Delete Policy?</AlertDialogTitle>
                        <AlertDialogDescription className="text-[#9CA3AF]">
                          This action cannot be undone. This will permanently delete the policy.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="secondary-btn">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          className="kill-switch-btn"
                          onClick={() => handleDelete(policy.id)}
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PoliciesPage;
